import { useState, type FormEvent } from "react";

import type { Curriculum, Subject, SubjectInput } from "../../services/curriculumService";
import AIAssistField from "../AIAssistField";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { CURRICULUM_TYPES, EDUCATION_LEVELS, SUBJECT_CATEGORIES, levelLabel } from "./academicsLogic";

/**
 * Create and edit forms for subjects and curricula. Save sits in the modal
 * footer; `record` switches a form to editing.
 */

const text = (data: FormData, name: string) => String(data.get(name) ?? "").trim();

export function SubjectForm({ record, curricula, busy, error, onSave, onClose }: {
  record?: Subject;
  curricula: Curriculum[];
  busy: boolean;
  error?: string;
  onSave: (data: SubjectInput) => void;
  onClose: () => void;
}) {
  // Inactive curricula stay listed only for a subject already in one.
  const choices = curricula.filter((item) => item.is_active || item.id === record?.curriculum);
  const [curriculum, setCurriculum] = useState(String(record?.curriculum ?? (choices.length === 1 ? choices[0].id : "")));
  const [name, setName] = useState(record?.name ?? "");
  const [code, setCode] = useState(record?.code ?? "");
  const [category, setCategory] = useState(record?.category ?? "core");
  const [description, setDescription] = useState(record?.description ?? "");
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    onSave({
      curriculum: Number(curriculum),
      name: name.trim(),
      code: code.trim().toUpperCase(),
      category,
      weekly_periods: Number(data.get("weekly_periods")),
      assessment_weight: text(data, "assessment_weight"),
      passing_grade: text(data, "passing_grade"),
      is_compulsory: data.get("is_compulsory") === "on",
      is_examinable: data.get("is_examinable") === "on",
      description: description.trim(),
      textbooks: text(data, "textbooks"),
      reference_materials: text(data, "reference_materials"),
      equipment_required: text(data, "equipment_required"),
    });
  };
  return (
    <FormModal
      id="subject-form"
      title={record ? `Edit ${record.name}` : "New subject"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create subject"}
      note="Subjects become choices for syllabi, timetables and exams."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Subject" divided={false} columns={2}>
        <Field label="Curriculum" className="sm:col-span-2">
          <select required value={curriculum} onChange={(event) => setCurriculum(event.target.value)} className="input mt-1">
            <option value="" disabled>Choose a curriculum</option>
            {choices.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} · {levelLabel(item.education_level)}{item.is_active ? "" : " (inactive)"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Name">
          <input required autoFocus value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Mathematics" />
        </Field>
        <Field label="Code" hint="Unique across the school, shown in capitals.">
          <input required maxLength={20} value={code} onChange={(event) => setCode(event.target.value)} className="input mt-1 uppercase" placeholder="e.g. MATH-P" />
        </Field>
        <Field label="Category">
          <select value={category} onChange={(event) => setCategory(event.target.value)} className="input mt-1">
            {SUBJECT_CATEGORIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Periods a week">
          <input required type="number" min={1} max={30} name="weekly_periods" defaultValue={record?.weekly_periods ?? 5} className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>

      <FormSection title="Assessment" description="How the subject counts in reports." columns={2}>
        <Field label="Weight in the overall result (%)">
          <input required type="number" min={0} max={100} step="any" name="assessment_weight" defaultValue={record ? Number(record.assessment_weight) : 100} className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Pass mark (%)">
          <input required type="number" min={0} max={100} step="any" name="passing_grade" defaultValue={record ? Number(record.passing_grade) : 50} className="input mt-1 tabular-nums" />
        </Field>
        {([
          ["is_compulsory", record?.is_compulsory ?? true, "Compulsory", "Every learner at the levels it is taught takes it."],
          ["is_examinable", record?.is_examinable ?? true, "Examinable", "It is set in exams and appears on report cards."],
        ] as const).map(([field, checked, label, help]) => (
          <label key={field} className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
            <input type="checkbox" name={field} defaultChecked={checked} className="mt-0.5 h-4 w-4 accent-primary-600" />
            <span>
              <span className="block font-medium text-gray-900">{label}</span>
              <span className="block text-xs text-gray-500">{help}</span>
            </span>
          </label>
        ))}
      </FormSection>

      <FormSection title="What it covers" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            name="description"
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="What learners study in this subject"
            context={{ key: "curriculum.setup_description", params: { kind: "subject", id: record?.id, name, code, category, curriculum: curriculum || null } }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the name, category, curriculum and the subjects beside it." : "Name the subject first, then draft."}
          />
        </Field>
      </FormSection>

      <FormSection title="Materials" description="Optional; one per line." columns={1}>
        <Field label="Textbooks" optional>
          <textarea name="textbooks" rows={2} defaultValue={record?.textbooks ?? ""} className="input mt-1" />
        </Field>
        <Field label="Reference materials" optional>
          <textarea name="reference_materials" rows={2} defaultValue={record?.reference_materials ?? ""} className="input mt-1" />
        </Field>
        <Field label="Equipment needed" optional>
          <textarea name="equipment_required" rows={2} defaultValue={record?.equipment_required ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

export function CurriculumForm({ record, busy, error, onSave, onClose }: {
  record?: Curriculum;
  busy: boolean;
  error?: string;
  onSave: (data: Partial<Curriculum>) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(record?.name ?? "");
  const [type, setType] = useState(record?.curriculum_type ?? "national");
  const [level, setLevel] = useState(record?.education_level ?? "primary");
  const [version, setVersion] = useState(record?.version ?? "");
  const [country, setCountry] = useState(record?.country ?? "Uganda");
  const [language, setLanguage] = useState(record?.language ?? "English");
  const [start, setStart] = useState(record?.start_date ?? "");
  const [end, setEnd] = useState(record?.end_date ?? "");
  const [description, setDescription] = useState(record?.description ?? "");
  const wrongWay = Boolean(start && end && end < start);
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (wrongWay) return;
    const data = new FormData(event.currentTarget);
    onSave({
      name: name.trim(),
      curriculum_type: type,
      education_level: level,
      version: version.trim(),
      country: country.trim(),
      language: language.trim(),
      start_date: start,
      end_date: end || null,
      is_mandatory: data.get("is_mandatory") === "on",
      curriculum_authority: text(data, "curriculum_authority"),
      description: description.trim(),
      objectives: text(data, "objectives"),
      notes: text(data, "notes"),
    });
  };
  return (
    <FormModal
      id="curriculum-form"
      title={record ? `Edit ${record.name}` : "New curriculum"}
      busy={busy}
      error={error}
      saveLabel={record ? "Save changes" : "Create curriculum"}
      note="A curriculum groups the subjects taught at one level of schooling."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="Curriculum" divided={false} columns={2}>
        <Field label="Name" className="sm:col-span-2">
          <input required autoFocus value={name} onChange={(event) => setName(event.target.value)} className="input mt-1" placeholder="e.g. Uganda National Primary Curriculum" />
        </Field>
        <Field label="Type">
          <select value={type} onChange={(event) => setType(event.target.value)} className="input mt-1">
            {CURRICULUM_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Level of schooling">
          <select value={level} onChange={(event) => setLevel(event.target.value)} className="input mt-1">
            {EDUCATION_LEVELS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Version" hint="The edition in use, e.g. the year it was issued.">
          <input required value={version} onChange={(event) => setVersion(event.target.value)} className="input mt-1" placeholder="e.g. 2026" />
        </Field>
        <Field label="Issued by" optional>
          <input name="curriculum_authority" defaultValue={record?.curriculum_authority ?? ""} className="input mt-1" placeholder="e.g. NCDC" />
        </Field>
        <Field label="Country">
          <input required value={country} onChange={(event) => setCountry(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="Language of teaching">
          <input required value={language} onChange={(event) => setLanguage(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="In use from">
          <input required type="date" value={start} onChange={(event) => setStart(event.target.value)} className="input mt-1" />
        </Field>
        <Field label="In use until" optional hint={wrongWay ? <span className="text-red-700">This is before it starts.</span> : "Leave blank while it is current."}>
          <input type="date" min={start || undefined} value={end} onChange={(event) => setEnd(event.target.value)} aria-invalid={wrongWay} className="input mt-1" />
        </Field>
        <label className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm sm:col-span-2 has-[:checked]:border-primary-500 has-[:checked]:bg-primary-50">
          <input type="checkbox" name="is_mandatory" defaultChecked={record?.is_mandatory ?? true} className="mt-0.5 h-4 w-4 accent-primary-600" />
          <span>
            <span className="block font-medium text-gray-900">Required by the education authority</span>
            <span className="block text-xs text-gray-500">Untick for a programme the school chooses to offer.</span>
          </span>
        </label>
      </FormSection>

      <FormSection title="About it" columns={1}>
        <Field as="div" label="Description" optional>
          <AIAssistField
            name="description"
            rows={3}
            value={description}
            onChange={(next) => setDescription(next)}
            placeholder="Which learners it serves and what kind of programme it is"
            context={{
              key: "curriculum.setup_description",
              params: { kind: "curriculum", id: record?.id, name, curriculum_type: type, education_level: level, version, country, language },
            }}
            draftLabel="Draft description"
            draftHint={name.trim() ? "Uses the name, type, level and country above." : "Name the curriculum first, then draft."}
          />
        </Field>
        <Field label="Objectives" optional hint="What learners should be able to do by the end, one per line.">
          <textarea name="objectives" rows={3} defaultValue={record?.objectives ?? ""} className="input mt-1" />
        </Field>
        <Field label="Notes" optional>
          <textarea name="notes" rows={2} defaultValue={record?.notes ?? ""} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}
